/**
 * Series grouping.
 *
 * A recurring event is one event that happens repeatedly, not twenty events.
 * The database stores it as twenty rows sharing a `patternId` because dates
 * have to be queryable, but counting those rows as twenty events is a
 * presentation bug: `extendRecurringSeries` keeps 20 upcoming occurrences per
 * series, so a monthly dance reaches two years out and a calendar of eleven
 * real events reports two hundred.
 *
 * Occurrences are grouped no matter how much their details differ. A venue
 * with a different band or theme every week is still running one event, and
 * splitting those out — by diffing fields, or by any "this one is special"
 * flag — would fragment exactly the series it was meant to clarify. Per-date
 * detail surfaces through `next` instead, which carries that occurrence's own
 * title, description and times.
 */

import {
  describeRule,
  joinOrdinals,
  DAY_NAMES,
  type Recurrence,
} from "convex/recurrenceLabel";

/** The fields of an event this module needs. */
export type SeriesEvent = {
  _id: string;
  date: string; // ISO "2026-02-15"
  patternId?: string;
  venueId?: string;
  venueName: string;
  venueLatitude?: number;
  venueLongitude?: number;
  recurrence?: Recurrence;
};

export type Cadence =
  | "weekly"
  | "biweekly"
  | "monthly"
  | "irregular"
  | "one-off";

export type Series<T extends SeriesEvent> = {
  /** Stable key: the pattern when there is one, else the event's own id. */
  key: string;
  /** The soonest upcoming occurrence — what the UI shows and links to. */
  next: T;
  /** Every upcoming occurrence, date-ascending. `next` is the first. */
  occurrences: T[];
  cadence: Cadence;
  /** True when this is a real series rather than a single dated event. */
  isRecurring: boolean;
};

const DAY_MS = 86_400_000;

function parseISO(date: string): number {
  // Parsed as UTC so a local timezone cannot shift the day and skew the gap.
  return Date.parse(`${date}T00:00:00Z`);
}

/** Median gap in days, which ignores the odd skipped or moved date. */
function medianGapDays(dates: string[]): number | null {
  if (dates.length < 2) return null;
  const gaps: number[] = [];
  for (let i = 1; i < dates.length; i++) {
    gaps.push((parseISO(dates[i]!) - parseISO(dates[i - 1]!)) / DAY_MS);
  }
  gaps.sort((a, b) => a - b);
  const mid = Math.floor(gaps.length / 2);
  return gaps.length % 2 ? gaps[mid]! : (gaps[mid - 1]! + gaps[mid]!) / 2;
}

/**
 * Works the cadence out from the dates themselves.
 *
 * The pattern rows hold this authoritatively, but no public query exposes them
 * — and with up to 20 occurrences to look at, the dates are a reliable enough
 * source for a label. Month lengths vary, so "monthly" is a range.
 */
export function describeCadence(dates: string[]): Cadence {
  if (dates.length <= 1) return "one-off";
  const gap = medianGapDays(dates);
  if (gap === null) return "one-off";
  if (gap >= 6 && gap <= 8) return "weekly";
  if (gap >= 13 && gap <= 16) return "biweekly";
  if (gap >= 27 && gap <= 32) return "monthly";
  return "irregular";
}

export const CADENCE_LABEL: Record<Cadence, string> = {
  weekly: "Weekly",
  biweekly: "Every other week",
  monthly: "Monthly",
  irregular: "Recurring",
  "one-off": "One-off",
};

/**
 * How the series reads in a listing: "Fridays", "1st Friday", "2nd & 4th
 * Sunday".
 *
 * Dancers think in weekdays, not frequencies — a monthly is "first Friday",
 * never "the 5th of the month" — so the label names the day rather than the
 * cadence. The rule on the pattern is authoritative when it reaches us;
 * otherwise the dates are read directly, which still covers anything with a
 * steady weekday.
 */
export function seriesLabel<T extends SeriesEvent>(series: Series<T>): string {
  if (!series.isRecurring) return CADENCE_LABEL["one-off"];

  const rule = series.next.recurrence;
  const fromRule = rule ? describeRule(rule) : null;
  return fromRule ?? labelFromDates(series.occurrences.map((e) => e.date));
}

function labelFromDates(dates: string[]): string {
  const cadence = describeCadence(dates);
  const weekdays = new Set(
    dates.map((d) => new Date(`${d}T00:00:00Z`).getUTCDay())
  );
  if (weekdays.size !== 1) return CADENCE_LABEL[cadence];
  const day = DAY_NAMES[[...weekdays][0]!]!;

  switch (cadence) {
    case "weekly":
      return `${day}s`;
    case "biweekly":
      return `Every other ${day}`;
    case "monthly": {
      const ordinals = new Set(
        dates.map((d) =>
          Math.ceil(Number(d.slice(8, 10)) / 7)
        )
      );
      return ordinals.size === 1
        ? `${joinOrdinals([...ordinals])} ${day}`
        : `Monthly on ${day}`;
    }
    default:
      return CADENCE_LABEL[cadence];
  }
}

/**
 * Collapses occurrences into one entry per series, ordered by next occurrence.
 *
 * Events without a `patternId` stand alone, so a genuine one-off is never
 * merged with anything.
 */
export function groupIntoSeries<T extends SeriesEvent>(events: T[]): Series<T>[] {
  const groups = new Map<string, T[]>();
  for (const event of events) {
    const key = event.patternId ?? `event:${event._id}`;
    const existing = groups.get(key);
    if (existing) existing.push(event);
    else groups.set(key, [event]);
  }

  const series: Series<T>[] = [];
  for (const [key, group] of groups) {
    group.sort((a, b) => a.date.localeCompare(b.date));
    const cadence = describeCadence(group.map((e) => e.date));
    series.push({
      key,
      next: group[0]!,
      occurrences: group,
      cadence,
      // A pattern with one remaining occurrence still reads as a one-off to a
      // reader looking at the list, so go by what is actually there.
      isRecurring: group.length > 1,
    });
  }

  return series.sort((a, b) => a.next.date.localeCompare(b.next.date));
}

/** ISO date `months` from today, for "in the next N months" windows. */
export function isoMonthsFromNow(months: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

/** How many occurrences fall on or before `throughDate`. */
export function countThrough<T extends SeriesEvent>(
  series: Series<T>,
  throughDate: string
): number {
  return series.occurrences.filter((e) => e.date <= throughDate).length;
}

/**
 * Counts for the list header. "11 events · 10 recurring, 1 one-off" is the
 * honest version of "201 events".
 */
export function summarize<T extends SeriesEvent>(series: Series<T>[]) {
  const recurring = series.filter((s) => s.isRecurring).length;
  return { total: series.length, recurring, oneOff: series.length - recurring };
}

/** One entry per venue, for map pins. Falls back to name when unlinked. */
export function groupByVenue<T extends SeriesEvent>(events: T[]) {
  const groups = new Map<string, T[]>();
  for (const event of events) {
    if (!event.venueLatitude || !event.venueLongitude) continue;
    const key =
      event.venueId ??
      `${event.venueName}@${event.venueLatitude},${event.venueLongitude}`;
    const existing = groups.get(key);
    if (existing) existing.push(event);
    else groups.set(key, [event]);
  }

  return Array.from(groups.entries()).map(([key, group]) => {
    group.sort((a, b) => a.date.localeCompare(b.date));
    return {
      key,
      venueName: group[0]!.venueName,
      venueId: group[0]!.venueId,
      lat: group[0]!.venueLatitude!,
      lng: group[0]!.venueLongitude!,
      events: group,
      series: groupIntoSeries(group),
    };
  });
}
